/* eslint-disable @next/next/no-img-element */
/* eslint-disable no-unused-vars */
/* eslint-disable react/no-unknown-property */
import { useEffect, useRef, useState } from 'react';

type ChannelMessage = {
    id?: string;
    body?: string;
    fromMe?: boolean;
    createdAt?: string;
    ack?: number;
    mediaType?: string;
    mediaUrl?: string;
};

declare global {
    interface Window {
        WEBCHAT_DEBUG?: boolean;
    }
}

const WABA_ID = 'ad98dead-8bc2-4b6a-b8e9-1bda8d8a881a';
const WEBSOCKET_TOKEN = 'f7acf46f-7f2f-4efc-b3e0-42ce37e4b7f2';
const TENANT_ID = '10';
const API_URL = 'https://api.alice.api.br';

const generateUniqueId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `${timestamp}-${random}`;
};

const generateSessionId = () => {
    if (!sessionStorage.getItem('channelWebchatId')) {
        sessionStorage.setItem('channelWebchatId', generateUniqueId());
    }

    return sessionStorage.getItem('channelWebchatId') || generateUniqueId();
};

const formatTime = (dateString?: string) => {
    const date = dateString ? new Date(dateString) : new Date();
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

const formatWhatsapp = (text?: string) => {
    const safeText = escapeHtml(String(text || ''));
    return safeText
        .replace(/\*(.*?)\*/g, '<b>$1</b>')
        .replace(/NEW LINE/gi, '<br>')
        .replace(/\\n/g, '<br>')
        .replace(/\n/g, '<br>');
};

const getAckIcon = (ack?: number) => {
    if (ack === 0) return '🕓';
    if (ack === 1) return '✔';
    if (ack === 2) return '✔';
    if (ack === 3) return '✔✔';
    if (ack === -1) return '✕';
    return '';
};

const buildMediaUrl = (mediaUrl?: string) => {
    if (!mediaUrl) return '';
    if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) return mediaUrl;
    return `${API_URL}/public/${TENANT_ID}/${mediaUrl}`;
};

const sanitizeFileName = (filename?: string) => {
    if (!filename) return '';
    return filename
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9.\-_]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
};

const classifyMediaType = (file: File) => {
    const type = String(file.type || '').toLowerCase();
    if (type.indexOf('image/') === 0) return 'image';
    if (type.indexOf('video/') === 0) return 'video';
    if (type.indexOf('audio/') === 0) return 'audio';
    return 'document';
};

const ChannelChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<ChannelMessage[]>([]);
    const [webchatId, setWebchatId] = useState('');
    const [token, setToken] = useState('');
    const [loaded, setLoaded] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const messagesRef = useRef<HTMLDivElement>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const tokenRef = useRef('');
    const webchatIdRef = useRef('');

    useEffect(() => {
        tokenRef.current = token;
    }, [token]);

    useEffect(() => {
        webchatIdRef.current = webchatId;
    }, [webchatId]);

    useEffect(() => {
        if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
        }
    }, [messages, open]);

    useEffect(() => {
        registerWebchat();

        return () => {
            wsRef.current?.close();
        };
    }, []);

    const registerWebchat = async () => {
        const sessionId = generateSessionId();
        const response = await fetch(`${API_URL}/webchat/register/${WABA_ID}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-websocket-token': WEBSOCKET_TOKEN
            },
            body: JSON.stringify({
                webchatId: sessionId,
                name: `WebChat ${sessionId}`,
                email: 'webchat@webchat.com',
                tenantId: TENANT_ID
            })
        });
        const data = await response.json();
        setWebchatId(sessionId);
        setToken(data.token);
        return { webchatId: sessionId, token: data.token };
    };

    const ensureFreshToken = async () => {
        const currentToken = tokenRef.current;
        if (!currentToken) return registerWebchat();

        try {
            const payload = JSON.parse(atob(currentToken.split('.')[1]));
            const expiresAt = (payload.exp || 0) * 1000;
            if (expiresAt - Date.now() < 5 * 60 * 1000) return registerWebchat();
        } catch {
            return registerWebchat();
        }

        return { webchatId: webchatIdRef.current, token: currentToken };
    };

    const loadMessageHistory = async () => {
        try {
            const session = await ensureFreshToken();
            const response = await fetch(
                `${API_URL}/webchat/messages/${WABA_ID}?from=${session.webchatId}&tenantId=${TENANT_ID}`,
                { headers: { Authorization: `Bearer ${session.token}` } }
            );
            const data = await response.json();
            if (Array.isArray(data)) setMessages(data);
        } catch (error) {
            if (window.WEBCHAT_DEBUG === true) console.error('[WebChat] Erro ao carregar histórico:', error);
        }
    };

    const connectWebSocket = async () => {
        const session = await ensureFreshToken();
        if (!session.webchatId || !session.token || wsRef.current?.readyState === WebSocket.OPEN) return;

        let reconnectAttempts = 0;
        let pingInterval: ReturnType<typeof setInterval>;
        let historyInterval: ReturnType<typeof setInterval>;

        const connect = () => {
            const ws = new WebSocket(`${API_URL.replace('https', 'wss')}/wss?from=${session.webchatId}&token=${session.token}`);
            wsRef.current = ws;

            ws.onopen = () => {
                reconnectAttempts = 0;
                pingInterval = setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
                }, 30000);
                historyInterval = setInterval(loadMessageHistory, 60000);
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'webhook' && data.payload?.message) {
                        setMessages((current) => [...current, data.payload.message]);
                    }
                    if (data.type === 'ack_update' && data.payload) {
                        setMessages((current) =>
                            current.map((message) =>
                                message.id === data.payload.messageId || message.id === data.payload.id
                                    ? { ...message, ...data.payload }
                                    : message
                            )
                        );
                    }
                } catch (error) {
                    if (window.WEBCHAT_DEBUG === true) console.error('[WebChat] Erro ao processar mensagem:', error);
                }
            };

            ws.onclose = () => {
                clearInterval(pingInterval);
                clearInterval(historyInterval);
                if (reconnectAttempts < 5) {
                    reconnectAttempts += 1;
                    setTimeout(connect, 5000);
                }
            };
        };

        connect();
    };

    const openChat = async () => {
        setOpen(true);
        if (!loaded) {
            await loadMessageHistory();
            await connectWebSocket();
            setLoaded(true);
        }
    };

    const sendMessage = async () => {
        const message = input.trim();
        if (!message) return;

        const tempMessage: ChannelMessage = {
            id: `temp-${Math.random().toString(36).substr(2, 9)}`,
            body: message,
            fromMe: false,
            createdAt: new Date().toISOString(),
            ack: 0
        };
        setMessages((current) => [...current, tempMessage]);
        setInput('');

        try {
            const session = await ensureFreshToken();
            const response = await fetch(`${API_URL}/webchat-webhook/${WABA_ID}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.token}`
                },
                body: JSON.stringify({
                    body: message,
                    from: session.webchatId,
                    name: session.webchatId,
                    email: `${session.webchatId}@webchat.com`,
                    tenantId: TENANT_ID,
                    event: 'messages.upsert',
                    fromMe: false,
                    channel: 'webchat',
                    type: 'webchat',
                    webchatId: session.webchatId
                })
            });
            const data = await response.json();
            if (data?.id) {
                setMessages((current) =>
                    current.map((item) => (item.id === tempMessage.id ? { ...item, id: data.id } : item))
                );
            }
            await loadMessageHistory();
        } catch (error) {
            if (window.WEBCHAT_DEBUG === true) console.error('[WebChat] Erro ao enviar mensagem:', error);
        }
    };

    const sendMedia = async (file: File) => {
        const session = await ensureFreshToken();
        const sanitizedFileName = sanitizeFileName(file.name);
        const mediaType = classifyMediaType(file);
        const formData = new FormData();
        const body = `caption: ${input.trim() || 'Mídia enviada'}`;

        formData.append('medias', file, sanitizedFileName);
        formData.append(
            'data',
            JSON.stringify({
                body,
                from: session.webchatId,
                name: session.webchatId,
                email: `${session.webchatId}@webchat.com`,
                tenantId: TENANT_ID,
                event: 'messages.upsert',
                fromMe: false,
                channel: 'webchat',
                type: 'webchat',
                webchatId: session.webchatId,
                mediaType,
                fileName: sanitizedFileName
            })
        );

        try {
            const response = await fetch(`${API_URL}/webchat-webhook/${WABA_ID}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${session.token}` },
                body: formData
            });
            if (!response.ok) throw new Error('Erro ao enviar mídia');
            setInput('');
            await loadMessageHistory();
        } catch (error) {
            if (window.WEBCHAT_DEBUG === true) console.error('[WebChat] Erro ao enviar mídia:', error);
            alert('Erro ao enviar mídia. Por favor, tente novamente.');
        }
    };

    const clearSession = async () => {
        if (!confirm('Tem certeza que deseja limpar a sessão e começar uma nova conversa?')) return;

        setMessages([]);
        sessionStorage.removeItem('channelWebchatId');
        wsRef.current?.close();
        setLoaded(false);
        await registerWebchat();
        await loadMessageHistory();
        await connectWebSocket();
    };

    const renderMessageContent = (message: ChannelMessage) => {
        const body = message.body || '';
        const caption = body.startsWith('caption: ') ? body.substring(9) : '';
        const mediaUrl = buildMediaUrl(message.mediaUrl);

        if (message.mediaType === 'location') {
            return (
                <a href={/^https?:\/\//i.test(body) ? body : '#'} target="_blank" rel="noopener noreferrer" className="channel-media-document">
                    <span>📍</span>
                    <strong>Localização</strong>
                </a>
            );
        }

        if (message.mediaType && mediaUrl) {
            if (message.mediaType.toLowerCase() === 'image') {
                return (
                    <div className="channel-media">
                        <img src={mediaUrl} alt="Imagem" onClick={() => window.open(mediaUrl, '_blank')} />
                        {caption ? <div className="channel-media-caption" dangerouslySetInnerHTML={{ __html: formatWhatsapp(caption) }} /> : null}
                    </div>
                );
            }
            if (message.mediaType.toLowerCase() === 'video') {
                return (
                    <div className="channel-media">
                        <video controls>
                            <source src={mediaUrl} type="video/mp4" />
                        </video>
                    </div>
                );
            }
            if (message.mediaType.toLowerCase() === 'audio') {
                return (
                    <div className="channel-media">
                        <audio controls>
                            <source src={mediaUrl} type="audio/mpeg" />
                        </audio>
                    </div>
                );
            }
            return (
                <a href={mediaUrl} className="channel-media-document" target="_blank" rel="noopener noreferrer">
                    <span>📄</span>
                    {caption || 'Documento'}
                </a>
            );
        }

        return <span dangerouslySetInnerHTML={{ __html: formatWhatsapp(body) }} />;
    };

    return (
        <>
            <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.2.96/css/materialdesignicons.min.css" rel="stylesheet" />
            <div id="channel-chat-widget">
                <button id="channel-chat-button" type="button" onClick={openChat} title="Abrir WEBChat">
                    <img src="https://app.alice.api.br/webchat-logo.png" alt="WebChat Logo" />
                </button>
            </div>
            <div id="channel-chat-container" className={open ? 'show' : ''}>
                <div id="channel-chat-header">
                    <h3>WEBChat</h3>
                    <span id="channel-chat-session">Sessão: {webchatId}</span>
                    <div className="channel-chat-header-actions">
                        <button id="channel-chat-clear" type="button" title="Nova sessão" onClick={clearSession}>
                            <i className="mdi mdi-reload" />
                        </button>
                        <button id="channel-chat-close" type="button" title="Fechar" onClick={() => setOpen(false)}>
                            <i className="mdi mdi-close" />
                        </button>
                    </div>
                </div>
                <div id="channel-chat-messages" ref={messagesRef}>
                    {messages.map((message, index) => {
                        const sent = message.fromMe === false;
                        return (
                            <div
                                key={message.id || index}
                                id={message.id ? `msg-${message.id}` : undefined}
                                className={`channel-message ${sent ? 'channel-sent' : 'channel-received'}`}
                            >
                                {renderMessageContent(message)}
                                <br />
                                <span className="channel-time">
                                    {formatTime(message.createdAt)} {sent ? <span className="channel-ack">{getAckIcon(message.ack)}</span> : null}
                                </span>
                            </div>
                        );
                    })}
                </div>
                <div id="channel-chat-input-area">
                    <input
                        type="text"
                        id="channel-chat-input"
                        placeholder="Digite sua mensagem..."
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') sendMessage();
                        }}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        id="channel-chat-file"
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) sendMedia(file);
                            event.target.value = '';
                        }}
                    />
                    <button id="channel-chat-attach" type="button" title="Anexar arquivo" onClick={() => fileInputRef.current?.click()}>
                        <i className="mdi mdi-paperclip" />
                    </button>
                    <button id="channel-chat-send" type="button" title="Enviar mensagem" onClick={sendMessage}>
                        <i className="mdi mdi-send" />
                    </button>
                </div>
            </div>
            <style jsx global>{`
                #channel-chat-widget {
                    position: fixed;
                    right: 82px;
                    bottom: 10px;
                    z-index: 101;
                }

                #channel-chat-button {
                    width: 60px;
                    height: 60px;
                    border: none;
                    border-radius: 50%;
                    background: #2196f3;
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.3s;
                    padding: 0;
                }

                #channel-chat-button:hover {
                    transform: scale(1.1);
                }

                #channel-chat-button img {
                    width: 30px;
                    height: 30px;
                }

                #channel-chat-container {
                    display: none;
                    position: fixed;
                    bottom: 82px;
                    right: 82px;
                    width: 320px;
                    height: 500px;
                    background: #fff;
                    border-radius: 16px;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
                    flex-direction: column;
                    z-index: 9998;
                }

                #channel-chat-container.show {
                    display: flex;
                }

                #channel-chat-header {
                    background: #2196f3;
                    color: white;
                    padding: 12px 16px;
                    border-radius: 16px 16px 0 0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 8px;
                }

                #channel-chat-header h3 {
                    margin: 0;
                    font-size: 16px;
                }

                #channel-chat-session {
                    flex: 1;
                    font-size: 11px;
                    color: #e3f2fd;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .channel-chat-header-actions {
                    display: flex;
                    gap: 8px;
                }

                #channel-chat-clear,
                #channel-chat-close,
                #channel-chat-attach,
                #channel-chat-send {
                    background: none;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                }

                #channel-chat-clear,
                #channel-chat-close {
                    color: white;
                    font-size: 18px;
                }

                #channel-chat-messages {
                    flex: 1;
                    overflow-y: auto;
                    padding: 16px;
                    background: #f7fafd;
                }

                #channel-chat-input-area {
                    display: flex;
                    padding: 12px;
                    background: #f7fafd;
                    border-top: 1px solid #e0e0e0;
                    align-items: center;
                }

                #channel-chat-input {
                    flex: 1;
                    min-width: 0;
                    padding: 8px;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    font-size: 14px;
                    outline: none;
                    margin-right: 8px;
                }

                #channel-chat-file {
                    display: none;
                }

                #channel-chat-attach,
                #channel-chat-send {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    color: #2196f3;
                    font-size: 20px;
                    transition: background-color 0.2s;
                }

                #channel-chat-attach:hover,
                #channel-chat-send:hover {
                    background-color: rgba(33, 150, 243, 0.1);
                }

                .channel-message {
                    max-width: 75%;
                    margin-bottom: 8px;
                    padding: 8px 12px;
                    border-radius: 16px;
                    word-break: break-word;
                    font-size: 14px;
                }

                .channel-sent {
                    background: #d1eaff;
                    margin-left: auto;
                    border-bottom-right-radius: 4px;
                }

                .channel-received {
                    background: #fff;
                    margin-right: auto;
                    border-bottom-left-radius: 4px;
                    border: 1px solid #e3f2fd;
                    box-shadow: 0 1px 2px rgba(33, 150, 243, 0.06);
                }

                .channel-time,
                .channel-ack {
                    font-size: 10px;
                    color: #888;
                }

                .channel-ack {
                    margin-left: 8px;
                }

                .channel-media {
                    max-width: 200px;
                    margin: 4px 0;
                }

                .channel-media img,
                .channel-media video {
                    width: 100%;
                    border-radius: 8px;
                    cursor: pointer;
                }

                .channel-media audio {
                    width: 100%;
                }

                .channel-media-document {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px;
                    background: #f5f5f5;
                    border-radius: 8px;
                    text-decoration: none;
                    color: #333;
                }

                .channel-media-caption {
                    margin-top: 4px;
                    font-size: 13px;
                    color: #666;
                    padding: 4px 8px;
                    background: #f5f5f5;
                    border-radius: 4px;
                }

                @media (max-width: 480px) {
                    #channel-chat-container {
                        width: 100%;
                        height: 100vh;
                        bottom: 0;
                        right: 0;
                        border-radius: 0;
                    }

                    #channel-chat-widget {
                        right: 82px;
                        bottom: 10px;
                    }
                }
            `}</style>
        </>
    );
};

export default ChannelChatWidget;
